import { useEffect, useMemo, useRef, useState } from 'react';
import { prefetchBuffers } from './mixEngine/bufferTransport';
import { L } from '../utils/logger';

type Track = { url: string; role?: string };

interface Options {
  scopeId: string | number | null | undefined;
  tracks: Track[] | undefined;
  tracksLoading: boolean;
  mix?: any;
}

/**
 * Tracks:
 * - preparing: track list loading or buffer prefetching
 * - playbackPending: waiting for first real time advance after selecting a scope
 * - needsGesture: autoplay blocked or start taking too long (only once tracks exist)
 */
export default function usePlayerPreparingState({ scopeId, tracks, tracksLoading, mix }: Options) {
  const [prefetching, setPrefetching] = useState(false);
  const [playbackPending, setPlaybackPending] = useState(false);
  const [needsGesture, setNeedsGesture] = useState(false);
  const [started, setStarted] = useState(false);
  // Progress: total/done/current/completed
  const [progress, setProgress] = useState<{ total: number; done: number; current: string | null; completed: string[] }>({
    total: 0,
    done: 0,
    current: null,
    completed: []
  });

  const playbackPendingRef = useRef(playbackPending);
  const lastScopeRef = useRef<typeof scopeId>(null);
  const startedOnceRef = useRef(false);
  const firstWaitMsRef = useRef<number | null>(null);
  const playingSettleMsRef = useRef<number | null>(null);
  // NEW: require seeing transport near zero after scope change before accepting first advance
  const seenNearZeroRef = useRef(false);
  const NEAR_ZERO = 0.10; // seconds
  // FIX: missing refs for autoplay fallback
  const autoStartArmedRef = useRef(false);
  const autoStartToRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const urls = useMemo(() => (tracks || []).map(t => t.url).filter(Boolean), [tracks]);
  const hasTracks = (tracks?.length ?? 0) > 0;

  useEffect(() => { playbackPendingRef.current = playbackPending; }, [playbackPending]);

  // Reset on scope change
  useEffect(() => {
    if (lastScopeRef.current !== scopeId) {
      lastScopeRef.current = scopeId ?? null;
      setPrefetching(false);
      startedOnceRef.current = false;
      setStarted(false);
      setPlaybackPending(Boolean(scopeId)); // arm pending when a scope is selected
      setNeedsGesture(false);
      firstWaitMsRef.current = null;
      // NEW: must see near-zero time for the new scope before marking started
      seenNearZeroRef.current = false;
      // reset progress
      setProgress({ total: 0, done: 0, current: null, completed: [] });
    }
  }, [scopeId]);

  // As soon as tracks exist for the selected scope and we haven't started yet,
  // assert playbackPending so "Starting…" appears immediately.
  useEffect(() => {
    if (!scopeId) return;
    if (!hasTracks) return;
    if (!startedOnceRef.current && !playbackPendingRef.current) {
      setPlaybackPending(true);
    }
  }, [scopeId, hasTracks]);

  // Prefetch decoded buffers for URLs of current scope WITH PROGRESS
  useEffect(() => {
    if (!scopeId) return;
    if (!urls.length) return;

    // Build entries with friendly names (filename only, no role prefix)
    const entries = (tracks || [])
      .filter(t => !!t.url)
      .map(t => {
        const url = t.url;
        const fn = (() => {
          try {
            const u = new URL(url, typeof window !== 'undefined' ? window.location.href : undefined);
            const last = u.pathname.split('/').filter(Boolean).pop() || url;
            return last;
          } catch {
            const parts = url.split('?')[0].split('/'); return parts[parts.length - 1] || url;
          }
        })();
        return { url, name: fn };
      });

    let canceled = false;
    (async () => {
      try {
        setPrefetching(true);
        // Reset progress for this batch
        setProgress({ total: entries.length, done: 0, current: null, completed: [] });

        // Try to get a usable AudioContext
        const actx = mix?.getMasterAnalyser?.()?.context as AudioContext | undefined;
        let ctx: AudioContext | undefined = actx;
        if (!ctx) {
          try {
            // eslint-disable-next-line @typescript-eslint/no-var-requires
            const { getSharedAudioContext } = require('./mixEngine/orchestrator/getSharedAudioContext');
            ctx = getSharedAudioContext();
          } catch {}
        }
        if (!ctx) return;

        // Sequential prefetch for deterministic current/completed updates
        for (const e of entries) {
          if (canceled) break;
          setProgress(prev => ({ ...prev, current: e.name }));
          try {
            await prefetchBuffers(ctx, [{ url: e.url }]);
          } catch (err) {
            L.warn('media:prefetch:item', 'failed', { scope: String(scopeId), url: e.url, err: (err as Error)?.message });
          }
          if (canceled) break;
          setProgress(prev => ({
            ...prev,
            done: Math.min(prev.done + 1, prev.total),
            completed: [...prev.completed, e.name]
          }));
        }
      } catch (e) {
        L.warn('media:prefetch', 'failed', { scope: String(scopeId), err: (e as Error)?.message });
      } finally {
        if (!canceled) setPrefetching(false);
      }
    })();

    return () => { canceled = true; };
  }, [scopeId, urls.join('|')]); // keep minimal deps; tracks used for names only

  // rAF loop: detect first real advance and when gesture is needed
  useEffect(() => {
    let raf: number | null = null;
    let lastT = 0;
    const MAX_START_MS = 1500;

    const tick = () => {
      try {
        const status = mix?.status as string | undefined;
        const t = Number(mix?.currentTime) || 0;
        const ctxState: AudioContextState | undefined = (() => {
          try { return mix?.getMasterAnalyser?.()?.context?.state as AudioContextState | undefined; } catch { return undefined; }
        })();

        const hasScope = !!scopeId;

        if (!hasScope) {
          // Reset when no selection
          if (playbackPendingRef.current) setPlaybackPending(false);
          startedOnceRef.current = false;
          setStarted(false);
          setNeedsGesture(false);
          firstWaitMsRef.current = null;
          seenNearZeroRef.current = false;
        } else if (!hasTracks) {
          // Tracks still loading: keep pending armed, not started
          if (!playbackPendingRef.current) setPlaybackPending(true);
          setStarted(false);
          setNeedsGesture(false);
          firstWaitMsRef.current = null;
          // Wait to observe near-zero on new scope
          seenNearZeroRef.current = false;
        } else {
          // Tracks available: expect start soon
          // NEW: only consider an advance valid after we've seen time near zero for this scope
          if (!seenNearZeroRef.current && t <= NEAR_ZERO) {
            seenNearZeroRef.current = true;
          }

          // Ensure pending stays ON while waiting for start
          if (!startedOnceRef.current && !playbackPendingRef.current) {
            setPlaybackPending(true);
          }

          const advanced = seenNearZeroRef.current && t > lastT + 0.02; // real forward motion after zero

          // START ONLY ON REAL ADVANCE
          if (!startedOnceRef.current && advanced) {
            startedOnceRef.current = true;
            setStarted(true);
            if (playbackPendingRef.current) setPlaybackPending(false);
            setNeedsGesture(false);
            firstWaitMsRef.current = null;
          } else if (!startedOnceRef.current) {
            // Before first advance: decide if gesture is needed
            if (ctxState === 'suspended') {
              setNeedsGesture(true);
            } else {
              if (firstWaitMsRef.current == null) firstWaitMsRef.current = Date.now();
              const waited = Date.now() - firstWaitMsRef.current;
              if (waited > MAX_START_MS) setNeedsGesture(true);
            }

            // If paused/stopped pre-start, clear pending
            if (status === 'paused' || status === 'stopped') {
              if (playbackPendingRef.current) setPlaybackPending(false);
              setStarted(false);
              setNeedsGesture(false);
              firstWaitMsRef.current = null;
              // keep seenNearZeroRef until next scope change
            }
          } else {
            // Once started, never re-arm pending/gesture
            setNeedsGesture(false);
          }
        }

        lastT = t;
      } catch { /* ignore */ }
      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => { if (raf != null) cancelAnimationFrame(raf); };
  }, [scopeId, hasTracks, mix]);

  // Autoplay fallback: try to start automatically; if blocked, arm a one-time gesture to start
  useEffect(() => {
    if (!scopeId || !hasTracks) return;
    // If we already started, nothing to do
    if (startedOnceRef.current) return;

    let canceled = false;
    const listeners: Array<[string, (e: any) => void]> = [];

    const disarmGesture = () => {
      if (!autoStartArmedRef.current) return;
      autoStartArmedRef.current = false;
      for (const [evt, handler] of listeners) {
        try { window.removeEventListener(evt, handler, true); } catch {}
      }
      listeners.length = 0;
    };

    const tryResumeContexts = async () => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const mod = require('./mixEngine/orchestrator/getSharedAudioContext');
        const g = mod?.getSharedAudioContext?.();
        if (g && g.state === 'suspended') { try { await g.resume(); } catch {} }
      } catch {}
      try {
        const an = mix?.getMasterAnalyser?.();
        const ctx = an?.context as AudioContext | undefined;
        if (ctx && ctx.state === 'suspended') { try { await ctx.resume(); } catch {} }
      } catch {}
    };

    const tryAutoStart = async () => {
      try {
        await tryResumeContexts();
        try { await (mix as any)?.preflightResetIfNeeded?.(); } catch {}
        await mix?.play?.();
      } catch { /* ignore */ }
    };

    const armGesture = () => {
      if (autoStartArmedRef.current) return;
      autoStartArmedRef.current = true;
      const handler = async () => {
        await tryAutoStart();
        disarmGesture();
      };
      for (const evt of ['pointerdown', 'touchstart', 'keydown', 'click']) {
        try {
          window.addEventListener(evt, handler, true);
          listeners.push([evt, handler]);
        } catch {}
      }
    };

    // First attempt immediately
    void tryAutoStart();

    // After a short grace, if not started or ctx still suspended, arm gesture
    autoStartToRef.current = setTimeout(() => {
      if (canceled || startedOnceRef.current) return;
      try {
        const an = mix?.getMasterAnalyser?.();
        const ctxState = an?.context?.state as AudioContextState | undefined;
        if (ctxState !== 'running') armGesture();
      } catch {
        armGesture();
      }
    }, 300);

    return () => {
      canceled = true;
      if (autoStartToRef.current) clearTimeout(autoStartToRef.current);
      autoStartToRef.current = null;
      disarmGesture();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeId, hasTracks, mix]);

  // NEW: derive preparing and showOverlay for consumers
  const preparing = Boolean(tracksLoading || prefetching);
  const showOverlay = preparing || (!!scopeId && !started);

  return {
    prefetching,
    playbackPending,
    needsGesture,
    started,
    progress,
    preparing,
    showOverlay
  };
}
